import React from "react";
import { Link, useParams } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import { RESOURCES, getResource } from "./resourcesData";
import Seo from "./Seo";
import ShareCard from "./ShareCard";
import { ArrowRight, ChevronLeft } from "lucide-react";

/** Educational hub — grid of all topics. */
export function ResourcesHub() {
  return (
    <MarketingShell>
      <Seo
        title="Resources — language for what may have felt nameless"
        description="Short, trauma-informed primers on gaslighting, DARVO, coercive control, trauma bonding, love bombing, the Cycle of Abuse, and the Power & Control Wheel."
        path="/resources"
      />
      <section className="max-w-5xl mx-auto px-6 sm:px-10 py-20" data-testid="page-resources">
        <p className="font-script text-2xl text-brand">a quiet library</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">Learn the language.</h1>
        <p className="mt-5 text-lg text-foreground/75 leading-relaxed max-w-2xl">
          Short, trauma-informed primers on the patterns and tactics that survivors most often want named.
          Each one links to the deeper guidance inside the Sister Signal app.
        </p>

        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {RESOURCES.map((r) => (
            <Link
              key={r.slug}
              to={`/resources/${r.slug}`}
              data-testid={`resource-link-${r.slug}`}
              className="rounded-3xl bg-card border border-border p-7 hover:border-brand/50 transition-all"
            >
              <p className="text-xs uppercase tracking-widest text-foreground/55">{r.kicker}</p>
              <h2 className="mt-1 font-serif text-2xl leading-snug">{r.title}</h2>
              <p className="mt-2 text-foreground/70 leading-relaxed">{r.lede}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand">
                Read <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </MarketingShell>
  );
}

/** Individual topic page — narrative + sections + soft CTA into the app. */
export function ResourceDetail() {
  const { slug } = useParams();
  const r = getResource(slug);
  if (!r) {
    return (
      <MarketingShell>
        <section className="max-w-3xl mx-auto px-6 sm:px-10 py-24 text-center">
          <p className="font-serif text-3xl">We couldn't find that topic.</p>
          <Link to="/resources" className="mt-6 inline-flex items-center gap-1 text-brand hover:underline">
            <ChevronLeft className="w-4 h-4" /> Back to resources
          </Link>
        </section>
      </MarketingShell>
    );
  }
  return (
    <MarketingShell>
      <Seo
        title={r.title}
        description={r.lede}
        path={`/resources/${slug}`}
        type="article"
      />
      <article className="max-w-3xl mx-auto px-6 sm:px-10 py-20" data-testid={`page-resource-${slug}`}>
        <Link to="/resources" className="inline-flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-6">
          <ChevronLeft className="w-4 h-4" /> Back to resources
        </Link>
        <p className="font-script text-2xl text-brand">{r.kicker}</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">{r.title}</h1>
        <p className="mt-5 text-xl text-foreground/80 leading-relaxed">{r.lede}</p>

        <div className="mt-10 space-y-7">
          {r.sections.map((s, i) => (
            <section key={i} className="rounded-3xl bg-card border border-border p-7">
              <p className="text-xs uppercase tracking-widest text-foreground/55">{s.h}</p>
              {s.body && <p className="mt-3 text-foreground/85 leading-relaxed">{s.body}</p>}
              {Array.isArray(s.list) && (
                <ul className="mt-3 space-y-2">
                  {s.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-foreground/85 leading-relaxed">
                      <span className="text-brand mt-1.5">·</span><span className="flex-1">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <section className="mt-10 rounded-3xl bg-brand-soft/40 border border-brand/20 p-8">
          <p className="font-script text-xl text-brand">when you're ready</p>
          <h3 className="mt-1 font-serif text-2xl leading-snug">Sister Signal has more on this.</h3>
          <p className="mt-2 text-foreground/75">The app has a private library, decoder, and reflection space for moments when you want to look closer.</p>
          <Link to="/app" data-testid="resource-open-app"
            className="mt-5 inline-flex items-center gap-1 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all">
            Open the app <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        <ShareCard
          title={r.title}
          slug={r.slug}
          url={`https://sistersignal.org/resources/${r.slug}`}
        />
      </article>
    </MarketingShell>
  );
}
